import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { IStand } from "@evennt-tech/api-v2-core";
import { chunk, uniq } from "lodash";
import { apiV2 } from "api/firebase";
import useInvalidateOnShowNotifications from "hooks/useInvalidateOnShowNotifications";
import {
  STAND_REALTIME_KEYS,
  STANDS_MAX_PAGE_SIZE,
  standsQueryKey,
} from "./standQueries";

type UseStandsByIdsArgs = {
  showId?: string;
  ids?: string[];
};

type UseStandsByIdsResult = [IStand[], boolean];

const useStandsByIds = ({
  showId,
  ids = [],
}: UseStandsByIdsArgs): UseStandsByIdsResult => {
  const sortedIds = useMemo(() => uniq(ids).sort(), [ids]);

  const { data, isFetching } = useQuery({
    queryKey: [...standsQueryKey(showId), "ids", sortedIds],
    queryFn: async () => {
      const pages = await Promise.all(
        chunk(sortedIds, STANDS_MAX_PAGE_SIZE).map((idsChunk) =>
          apiV2.stand.getPaginated({
            showId: showId as string,
            ids: idsChunk,
            limit: idsChunk.length,
          })
        )
      );

      return pages.flatMap((page) => page.items);
    },
    enabled: !!showId && sortedIds.length > 0,
    refetchOnWindowFocus: false,
  });

  useInvalidateOnShowNotifications({
    showId,
    keys: STAND_REALTIME_KEYS,
    queryKey: standsQueryKey(showId),
  });

  const stands = useMemo(() => (sortedIds.length ? data ?? [] : []), [
    data,
    sortedIds,
  ]);

  return [stands, isFetching];
};

export default useStandsByIds;
